import { useCallback, useContext } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { parseApiData } from "go-core/api/parseApiData";
import { useWindowSize } from "go-core/components/useWindowSize";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { selectUser } from "go-security/services/users/selectors";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { SegmentApi } from "go-segment/services/types";
import { getDefaultDashboardSegment } from "../../utils/utils";
import { DashboardChartActions } from "../charts/reducer";
import { DashboardEditActions } from "../editMode/reducer";
import { DashboardFilterActions } from "../filters/reducer";
import {
	DASHBOARD_RESOURCE_TYPE,
	GROUP_DASHBOARD_RESOURCE_TYPE,
	getDashboardSegmentNonMobileConfigForSegment,
} from "../segments/constants";
import { DashboardSegmentActions } from "../segments/reducer";
import { DashboardAllSegments, DashboardSegmentSaveFunctionMode, DashboardType } from "../types";
import useDashboardApi from "./useDashboardApi";
import useDashboardContext from "./useDashboardContext";

interface DashboardSegmentDetails {
	id?: string;
	name: string;
	resource_type: string;
	slug: string;
	default: boolean;
	list_name: string;
}

export const useDashboardSegment = () => {
	const {
		state,
		state: {
			segmentsConfig: {
				allSegments,
				selectedSegment: {
					value: selectedSegmentValue,
					value: {
						dashboardEdit: { nonMobileGridConfig: segmentGridConfig },
					},
				},
				selectedSegment,
			},
			chartsConfig,
			filtersConfig,
			dashboardEdit,
			dashboardEdit: { nonMobileGridConfig },
		},
		dispatch,
		dashboardType,
		setIsEditMode,
	} = useDashboardContext();
	const isUserDashboard = dashboardType === DashboardType.GROUP_ORGANIZATIONS;
	const segmentContext = useContext(SegmentContext);
	const history = useHistory();
	const { pathname, search } = useLocation();
	const user = useSelector(selectUser);
	const organization = useSelector(selectOrganization);
	const resourceAndListName = isUserDashboard ? GROUP_DASHBOARD_RESOURCE_TYPE : DASHBOARD_RESOURCE_TYPE;
	const { fetchDataAndApplyNewDataToDashboardState } = useDashboardApi();
	const { isMobile } = useWindowSize();

	const didDashboardSegmentChange = () => {
		const currentDashboardStateValuesToBeComparedWithSegment = {
			chartsConfig,
			dashboardEdit: {
				mobileGridConfig: dashboardEdit.mobileGridConfig,
				nonMobileGridConfig: getDashboardSegmentNonMobileConfigForSegment(dashboardEdit.nonMobileGridConfig),
			},
			filtersConfig,
		};

		if (isMobile) {
			selectedSegmentValue.dashboardEdit.nonMobileGridConfig = getDashboardSegmentNonMobileConfigForSegment(
				selectedSegmentValue.dashboardEdit.nonMobileGridConfig
			);
		}

		const selectedSegmentValueAsString = JSON.stringify(selectedSegmentValue);
		const currentDashboardStateAsString = JSON.stringify(currentDashboardStateValuesToBeComparedWithSegment);

		return selectedSegmentValueAsString !== currentDashboardStateAsString;
	};

	const didDashboardSegmnetGridConfigChange = () => {
		const stateGridConfig = getDashboardSegmentNonMobileConfigForSegment(
			JSON.parse(JSON.stringify(nonMobileGridConfig))
		);

		const selectedSegmentGridConfigAsString = JSON.stringify(segmentGridConfig);
		const currentDashboardGridConfigAsString = JSON.stringify(stateGridConfig);

		return selectedSegmentGridConfigAsString !== currentDashboardGridConfigAsString;
	};

	const nonDefaultSegmentSetAsDefault = (segments: DashboardAllSegments[]): DashboardAllSegments | undefined =>
		segments.find((segment) => segment.slug !== "default" && segment.default);

	const parseSegmentsAndUpdateSegmentState = useCallback(
		(newSegments: SegmentApi[]) => {
			const parsedSegments: DashboardAllSegments[] = newSegments.map((segment) => {
				const { value: segmentValue } = segment;
				const isSegmentValueEncoded = typeof segmentValue === "string";

				if (isSegmentValueEncoded) {
					const decodedSegmentValue = JSON.parse(decodeURIComponent(escape(atob(segment.value))));

					return { ...segment, id: segment.id.toString(), value: decodedSegmentValue };
				}

				return { ...segment, id: segment.id.toString(), value: segmentValue };
			});

			const defaultSegmentAlreadyIncluded = newSegments.find((segment) => segment.slug === "default");

			if (!defaultSegmentAlreadyIncluded) {
				const defaultDashboardSegment = getDefaultDashboardSegment(isUserDashboard, user);

				if (nonDefaultSegmentSetAsDefault(parsedSegments)) {
					defaultDashboardSegment.default = false;
				}

				parsedSegments.unshift(defaultDashboardSegment);
			}

			dispatch({
				type: DashboardSegmentActions.SET_SEGMENTS,
				payload: parsedSegments,
			});

			return parsedSegments;
		},
		[dispatch, isUserDashboard, user]
	);

	const getFiltersConfigForSegmentToBeSaved = (saveOnlySelectedSegmentGridConfig: boolean) => {
		if (dashboardType === DashboardType.GROUP_ORGANIZATIONS) {
			return saveOnlySelectedSegmentGridConfig ? selectedSegment.value.filtersConfig : state.filtersConfig;
		}

		return saveOnlySelectedSegmentGridConfig
			? {
					...selectedSegment.value.filtersConfig,
					selectedFilter: {
						...selectedSegment.value.filtersConfig.selectedFilter,
						organizationIds: selectedSegment.value.filtersConfig.selectedFilter.organizationIds ?? "",
					},
			  }
			: {
					...state.filtersConfig,
					selectedFilter: {
						...state.filtersConfig.selectedFilter,
						organizationIds: state.filtersConfig.selectedFilter.organizationIds ?? "",
					},
			  };
	};

	const saveOrUpdateDashboardSegment = async (
		segmentDetails: DashboardSegmentDetails,
		functionMode: DashboardSegmentSaveFunctionMode,
		saveOnlySelectedSegmentGridConfig = false
	) => {
		const segmentDashboardObjectValueToBeSaved = {
			chartsConfig: saveOnlySelectedSegmentGridConfig ? selectedSegment.value.chartsConfig : state.chartsConfig,
			dashboardEdit: {
				mobileGridConfig: saveOnlySelectedSegmentGridConfig
					? selectedSegment.value.dashboardEdit.mobileGridConfig
					: state.dashboardEdit.mobileGridConfig,
				nonMobileGridConfig: getDashboardSegmentNonMobileConfigForSegment(
					state.dashboardEdit.nonMobileGridConfig
				),
			},
			filtersConfig: getFiltersConfigForSegmentToBeSaved(saveOnlySelectedSegmentGridConfig),
		};

		const encodedSegmentValue = btoa(
			unescape(encodeURIComponent(JSON.stringify(segmentDashboardObjectValueToBeSaved)))
		);

		const newSegment = {
			...segmentDetails,
			id: segmentDetails.id ? Number(segmentDetails.id) : undefined,
			value: encodedSegmentValue,
		} as SegmentApi;
		let segmentResponse: SegmentApi | null = null;
		const newSegmentFromResponseArray: SegmentApi[] = JSON.parse(JSON.stringify(allSegments));

		if (functionMode === DashboardSegmentSaveFunctionMode.SAVE_NEW_SEGMENT) {
			segmentResponse = await parseApiData(segmentContext.api().saveSegment(newSegment));
			if (!segmentResponse) return;

			newSegmentFromResponseArray.push(segmentResponse);
		} else {
			segmentResponse = await parseApiData(segmentContext.api().updateSegment(newSegment));
			if (!segmentResponse) return;

			const updatedSegmentIndex = newSegmentFromResponseArray.findIndex(
				(segment) => segment.slug === segmentResponse?.slug
			);

			if (updatedSegmentIndex !== -1) {
				newSegmentFromResponseArray.splice(updatedSegmentIndex, 1, segmentResponse);
			}
		}

		const parsedSegments = parseSegmentsAndUpdateSegmentState(newSegmentFromResponseArray);
		const newParsedSelectedSegment = parsedSegments.find((segment) => segment.slug === segmentResponse.slug);

		if (!saveOnlySelectedSegmentGridConfig) {
			dispatch({
				type: DashboardChartActions.CHANGE_ENTIRE_REPORT_CONFIG,
				data: newParsedSelectedSegment?.value.chartsConfig,
			});
			dispatch({
				type: DashboardFilterActions.CHANGE_ALL_SELECTED_FILTERS,
				data: newParsedSelectedSegment?.value.filtersConfig.selectedFilter,
			});
		}

		dispatch({
			type: DashboardEditActions.CHANGE_ENTIRE_GRID_CONFIG,
			payload: newParsedSelectedSegment?.value.dashboardEdit,
		});
		dispatch({
			type: DashboardSegmentActions.CHANGE_SELECTED_SEGMENT,
			payload: newParsedSelectedSegment,
		});
		setIsEditMode(false);
		const urlSearchParams = new URLSearchParams();
		urlSearchParams.set("s", segmentResponse.slug);

		history.push({
			pathname,
			search: urlSearchParams.toString(),
		});
	};

	const saveDashboardSegment = async (segment: SegmentType, saveOnlySelectedSegmentGridConfig = false) => {
		const newSegment = {
			id: segment.id,
			name: segment.name,
			resource_type: resourceAndListName,
			slug: segment.slug,
			default: false,
			list_name: resourceAndListName,
		};

		await saveOrUpdateDashboardSegment(
			newSegment,
			DashboardSegmentSaveFunctionMode.SAVE_EXISTING_SEGMENT,
			saveOnlySelectedSegmentGridConfig
		);
	};

	const createDashboardSegment = async (name: string) => {
		const newSegment = {
			name,
			resource_type: resourceAndListName,
			slug: name?.replaceAll(" ", "_"),
			default: false,
			list_name: resourceAndListName,
		};

		await saveOrUpdateDashboardSegment(newSegment, DashboardSegmentSaveFunctionMode.SAVE_NEW_SEGMENT);
	};

	const applySegmentToDashboardState = useCallback(
		(segment: DashboardAllSegments, applySegmentFiltersToDashboardState = true) => {
			const {
				value: { filtersConfig, chartsConfig, dashboardEdit },
			} = segment;

			dashboardEdit.nonMobileGridConfig = (dashboardEdit.nonMobileGridConfig ?? []).map((gridItem) => {
				const { i, x, y } = gridItem;
				const currenGridItemFromCurrentDashboardGridConfig = nonMobileGridConfig.find(
					(dashboardGridItem) => dashboardGridItem.i === gridItem.i
				);
				const w = currenGridItemFromCurrentDashboardGridConfig?.w ?? 1;

				return { i, x, y, w, h: 1 };
			});

			dispatch({ type: DashboardSegmentActions.CHANGE_SELECTED_SEGMENT, payload: segment });
			dispatch({ type: DashboardChartActions.CHANGE_ENTIRE_REPORT_CONFIG, data: chartsConfig });
			dispatch({ type: DashboardEditActions.CHANGE_ENTIRE_GRID_CONFIG, payload: dashboardEdit });

			if (applySegmentFiltersToDashboardState) {
				dispatch({
					type: DashboardFilterActions.CHANGE_ALL_SELECTED_FILTERS,
					data: filtersConfig.selectedFilter,
				});
			}
		},
		[dispatch, nonMobileGridConfig]
	);

	const segmentOptionClickHandler = async (segment: SegmentType | null) => {
		const urlSearchParams = new URLSearchParams(search);
		let newSegment = segment;

		if (!newSegment) {
			const defaultSegment = allSegments.find((dashboardSegment) => dashboardSegment.slug === "default");

			if (!defaultSegment) return;

			newSegment = defaultSegment;
		}

		if (!isMobile && newSegment.slug === urlSearchParams.get("s")) {
			return;
		}

		applySegmentToDashboardState(newSegment as DashboardAllSegments);

		const {
			value: {
				chartsConfig,
				filtersConfig: {
					selectedFilter: { dateRange, organizationIds },
				},
			},
		} = newSegment as DashboardAllSegments;

		if (!isMobile) {
			await fetchDataAndApplyNewDataToDashboardState({
				chartsConfig,
				selectedDateRange: dateRange,
				selectedOrganizationIds: organizationIds,
			});

			urlSearchParams.set("s", newSegment.slug);

			history.push({
				pathname: isUserDashboard ? "/dashboard" : `/${organization.id}`,
				search: urlSearchParams.toString(),
			});
		}
	};

	return {
		didDashboardSegmentChange,
		parseSegmentsAndUpdateSegmentState,
		applySegmentToDashboardState,
		segmentOptionClickHandler,
		saveDashboardSegment,
		createDashboardSegment,
		nonDefaultSegmentSetAsDefault,
		didDashboardSegmnetGridConfigChange,
	};
};
